import React, { useState, useEffect } from 'react';

const SessionOverridesModal = ({
  open,
  onClose,
  session,
  crewId,
  crew,
  assumedAvailableUserIdsForDate = [],
  existingSessionOverride = null,
  getUserInfo,
  onSave,
  isSaving,
}) => {
  const [overrideStart, setOverrideStart] = useState('06:00');
  const [fixedDuration, setFixedDuration] = useState(90);
  const [overrideOptions, setOverrideOptions] = useState([]);
  const [overrideSupporters, setOverrideSupporters] = useState([]);
  const [overrideMissing, setOverrideMissing] = useState([]);
  const [overrideSelections, setOverrideSelections] = useState({});
  const [selectedWindowIndex, setSelectedWindowIndex] = useState(0);

  // Utilities for time math
  const minutes = (hhmm) => {
    const [h, m] = (hhmm || '00:00').split(':').map((x) => parseInt(x, 10));
    return (h || 0) * 60 + (m || 0);
  };

  const formatHHMM = (mins) => {
    const mm = Math.max(0, mins) % (24 * 60);
    const h = Math.floor(mm / 60);
    const m = mm % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // From structured_feedback, compute supporters/missing for the selected window
  const computeSupportersAndMissing = (crew, session, startStr, duration) => {
    const sf = session?.structured_feedback || {};
    const timeSlots = Array.isArray(sf.time_slots) ? sf.time_slots : [];
    const fullAvail = new Set(
      Array.isArray(sf.full_availability_members) ? sf.full_availability_members : []
    );
    let crewMemberIds = Array.isArray(crew?.member_ids) ? crew.member_ids : [];
    const sMin = minutes(startStr);
    const eMin = sMin + duration;
    const supporters = new Set();
    // Full availability supports any window
    fullAvail.forEach((id) => supporters.add(id));
    // Per-day resolutions: assume_available
    const assumedSet = new Set(
      Array.isArray(assumedAvailableUserIdsForDate) ? assumedAvailableUserIdsForDate : []
    );
    assumedSet.forEach((uid) => supporters.add(uid));
    // Existing session overrides
    try {
      const mo = existingSessionOverride?.member_overrides || {};
      Object.entries(mo).forEach(([uidStr, cfg]) => {
        const uid = parseInt(uidStr, 10);
        if (!uid || cfg?.type !== 'available') return;
        const ranges = Array.isArray(cfg?.ranges) ? cfg.ranges : [];
        for (const r of ranges) {
          const rs = minutes(r.start_time || r.start || '00:00');
          const re = minutes(r.end_time || r.end || '00:00');
          if (rs <= sMin && re >= eMin) {
            supporters.add(uid);
            break;
          }
        }
      });
    } catch {
      // ignore
    }
    // Slot-based
    for (const slot of timeSlots) {
      const uid = slot.member_id;
      const ranges = Array.isArray(slot.time_ranges) ? slot.time_ranges : [];
      for (const r of ranges) {
        const rs = minutes(r.start_time || r.start || '00:00');
        const re = minutes(r.end_time || r.end || '00:00');
        if (rs <= sMin && re >= eMin) {
          supporters.add(uid);
          break;
        }
      }
    }
    // If crewMemberIds are not available, derive a best-effort member list from feedback
    if (!crewMemberIds || crewMemberIds.length === 0) {
      const derived = new Set();
      timeSlots.forEach((slot) => {
        if (typeof slot?.member_id === 'number') derived.add(slot.member_id);
      });
      fullAvail.forEach((id) => derived.add(id));
      crewMemberIds = Array.from(derived);
    }

    const supportersArr = crewMemberIds.filter((id) => supporters.has(id));
    const missingArr = crewMemberIds.filter((id) => !supporters.has(id));
    return { supporters: supportersArr, missing: missingArr };
  };

  const recomputeWindowMembers = (crewArg, session, startStr, duration) => {
    const crewObj = crewArg || {};
    const { supporters, missing } = computeSupportersAndMissing(
      crewObj,
      session,
      startStr,
      duration
    );
    setOverrideSupporters(supporters);
    setOverrideMissing(missing);
    setOverrideSelections((prev) => {
      const next = { ...prev };
      const endStr = formatHHMM(minutes(startStr) + duration);
      missing.forEach((uid) => {
        const current = next[uid] || { enabled: false };
        next[uid] = {
          enabled: Boolean(current.enabled),
          start: startStr,
          end: endStr,
        };
      });
      supporters.forEach((uid) => {
        if (next[uid]) delete next[uid];
      });
      return next;
    });
  };

  useEffect(() => {
    if (!open || !session) return;

    // Collect best + alternate overlaps
    const sf = session?.structured_feedback || {};
    const options = [];
    if (sf?.best_overlap?.start_time && sf?.best_overlap?.end_time) options.push(sf.best_overlap);
    if (Array.isArray(sf?.alternate_overlaps)) {
      sf.alternate_overlaps.forEach((o) => {
        if (o?.start_time && o?.end_time) options.push(o);
      });
    }
    setOverrideOptions(options);

    // Pick initial window
    const start = sf?.best_overlap?.start_time || '06:00';
    const end = sf?.best_overlap?.end_time || '08:00';
    const initialDuration = minutes(end) - minutes(start) || 90;
    setOverrideStart(start);
    setFixedDuration(initialDuration);

    // Compute supporters/missing and initialize selections
    const crewObj = crew || {};
    const { supporters, missing } = computeSupportersAndMissing(
      crewObj,
      session,
      start,
      initialDuration
    );
    setOverrideSupporters(supporters);
    setOverrideMissing(missing);
    const initSel = {};
    missing.forEach((uid) => {
      initSel[uid] = { enabled: false, start, end: formatHHMM(minutes(start) + initialDuration) };
    });
    setOverrideSelections(initSel);
    setSelectedWindowIndex(0);
  }, [open, session]);

  useEffect(() => {
    if (!open || !session) return;
    recomputeWindowMembers(crew, session, overrideStart, fixedDuration);
  }, [overrideStart, fixedDuration, open, session, crew]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/30">
      <div className="relative flex max-h-[80vh] w-full max-w-2xl flex-col rounded-lg bg-white shadow-xl">
        <div className="flex flex-shrink-0 items-center justify-between border-b px-4 py-3">
          <h3 className="text-base font-medium text-gray-900">Adjust Availability</h3>
          <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-4 py-3">
          <div className="mb-2 text-sm text-gray-600">
            <div className="font-medium">Date: {formatDate(session.session_date)}</div>
            <div className="mt-0.5 text-xs">
              Override availability for this session. Changes save automatically.
            </div>
          </div>

          <div className="mb-3 rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-800">
            <div className="mb-1 font-medium">How overrides work:</div>
            <ul className="list-disc space-y-0.5 pl-4 text-[11px]">
              <li>Session-scoped, doesn't change real availability</li>
              <li>Pick a suggested window or adjust start time</li>
              <li>Mark missing members as available in this window</li>
              <li>Supersedes per-day resolutions for this session only</li>
            </ul>
          </div>

          {overrideOptions.length > 0 && (
            <div className="mb-3">
              <div className="mb-1.5 text-xs font-medium text-gray-700">Suggested windows</div>
              <div className="flex flex-wrap gap-1.5">
                {overrideOptions.map((opt, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setSelectedWindowIndex(idx);
                      const s = opt.start_time;
                      const e = opt.end_time;
                      const dur = minutes(e) - minutes(s) || fixedDuration;
                      setOverrideStart(s);
                      setFixedDuration(dur);
                      recomputeWindowMembers(crew, session, s, dur);
                    }}
                    className={`${selectedWindowIndex === idx ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'} rounded-full px-2.5 py-1 text-xs`}
                    title={`${opt.member_count || ''} members`}
                  >
                    {opt.display || `${opt.start_time}-${opt.end_time}`}{' '}
                    {typeof opt.member_count === 'number' ? `(${opt.member_count})` : ''}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="mb-3 grid grid-cols-3 gap-2">
            <div>
              <label className="mb-1 block text-xs text-gray-600">Start time</label>
              <input
                type="time"
                value={overrideStart}
                onChange={(e) => setOverrideStart(e.target.value)}
                className="w-full rounded border px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600">Duration (min)</label>
              <input
                type="number"
                value={fixedDuration}
                readOnly
                className="w-full rounded border bg-gray-50 px-2 py-1 text-xs"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs text-gray-600">End time</label>
              <input
                type="time"
                value={formatHHMM(minutes(overrideStart) + fixedDuration)}
                readOnly
                className="w-full rounded border bg-gray-50 px-2 py-1 text-xs"
              />
            </div>
          </div>

          <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="rounded border p-2">
              <div className="mb-1.5 text-xs font-medium text-green-700">Supporting members</div>
              {overrideSupporters.length === 0 ? (
                <div className="text-xs text-gray-500">None</div>
              ) : (
                <ul className="space-y-0.5 text-xs text-gray-700">
                  {overrideSupporters.map((uid) => {
                    const info = getUserInfo(uid);
                    const name = info ? `${info.name}` : `User ${uid}`;
                    return (
                      <li key={uid} className="flex items-center justify-between">
                        <span>{name}</span>
                        <span className="rounded bg-green-100 px-1.5 py-0.5 text-[10px] text-green-700">
                          Available
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="rounded border p-2">
              <div className="mb-1.5 flex items-center justify-between">
                <div className="text-xs font-medium text-red-700">Missing members</div>
                <button
                  className="rounded bg-blue-600 px-2 py-0.5 text-xs text-white hover:bg-blue-700"
                  onClick={() => {
                    setOverrideSelections((prev) => {
                      const next = { ...prev };
                      const startStr = overrideStart;
                      const endStr = formatHHMM(minutes(overrideStart) + fixedDuration);
                      overrideMissing.forEach((uid) => {
                        next[uid] = { enabled: true, start: startStr, end: endStr };
                      });
                      return next;
                    });
                  }}
                >
                  Mark all available
                </button>
              </div>
              {overrideMissing.length === 0 ? (
                <div className="text-xs text-gray-500">None</div>
              ) : (
                <div className="space-y-2">
                  {overrideMissing.map((uid) => {
                    const sel = overrideSelections[uid] || {
                      enabled: false,
                      start: overrideStart,
                      end: formatHHMM(minutes(overrideStart) + fixedDuration),
                    };
                    const info = getUserInfo(uid);
                    const name = info ? `${info.name}` : `User ${uid}`;
                    const minStart = overrideStart;
                    const maxEnd = formatHHMM(minutes(overrideStart) + fixedDuration);
                    return (
                      <div key={uid} className="rounded border border-gray-100 p-1.5">
                        <label className="flex items-center gap-1.5 text-xs text-gray-800">
                          <input
                            type="checkbox"
                            checked={Boolean(sel.enabled)}
                            onChange={(e) => {
                              const checked = e.target.checked;
                              setOverrideSelections((prev) => ({
                                ...prev,
                                [uid]: {
                                  enabled: checked,
                                  start: sel.start || minStart,
                                  end: sel.end || maxEnd,
                                },
                              }));
                            }}
                          />
                          <span>{name}</span>
                        </label>
                        {sel.enabled && (
                          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
                            <div>
                              <label className="mb-0.5 block text-[10px] text-gray-600">From</label>
                              <input
                                type="time"
                                min={minStart}
                                max={maxEnd}
                                value={sel.start}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const clamped = minutes(v) < minutes(minStart) ? minStart : v;
                                  setOverrideSelections((prev) => ({
                                    ...prev,
                                    [uid]: { ...prev[uid], start: clamped },
                                  }));
                                }}
                                className="w-full rounded border px-1.5 py-0.5 text-xs"
                              />
                            </div>
                            <div>
                              <label className="mb-0.5 block text-[10px] text-gray-600">To</label>
                              <input
                                type="time"
                                min={minStart}
                                max={maxEnd}
                                value={sel.end}
                                onChange={(e) => {
                                  const v = e.target.value;
                                  const clamped = minutes(v) > minutes(maxEnd) ? maxEnd : v;
                                  setOverrideSelections((prev) => ({
                                    ...prev,
                                    [uid]: { ...prev[uid], end: clamped },
                                  }));
                                }}
                                className="w-full rounded border px-1.5 py-0.5 text-xs"
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-shrink-0 justify-end gap-2 border-t px-4 py-3">
          <button
            onClick={onClose}
            className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              const windowStart = overrideStart;
              const windowEnd = formatHHMM(minutes(overrideStart) + fixedDuration);
              const member_overrides = {};
              Object.entries(overrideSelections).forEach(([uid, sel]) => {
                if (sel && sel.enabled) {
                  const start = sel.start || windowStart;
                  const end = sel.end || windowEnd;
                  member_overrides[uid] = {
                    type: 'available',
                    ranges: [{ start, end }],
                  };
                }
              });
              const payload = {
                date: session.session_date.split('T')[0],
                duration_minutes: fixedDuration,
                member_overrides,
                // Enhanced metadata
                crew_id: crewId,
                crew_name: crew?.crew_name || `Crew ${crewId}`,
                boat_type: crew?.boat_type || 'Unknown',
                session_title: session.session_title || `Session ${session.session_id}`,
                created_at: new Date().toISOString(),
                created_by: 'user', // TODO: Add actual user ID when available
                override_type: 'session_availability',
                window_start: windowStart,
                window_end: windowEnd,
                total_members_affected: Object.keys(member_overrides).length,
                original_issue: session.reason || 'Scheduling conflict',
              };
              onSave(crewId, session.session_id, payload);
            }}
            disabled={isSaving}
            className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSaving ? 'Saving...' : 'Save Overrides'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SessionOverridesModal;
